// src/pages/admin/components/SpecializedManagersPanel.jsx

import React from 'react';
import { AdminAPI } from '../../../api/admin';
import { Card, Actions, Btn } from '../ui';

export default function SpecializedManagersPanel({ propertyId, propertyType }) {
  const [propertyManagers, setPropertyManagers] = React.useState([]);
  const [allManagers, setAllManagers] = React.useState([]);
  const [busy, setBusy] = React.useState(false);
  const [msg, setMsg] = React.useState('');
  const [showPicker, setShowPicker] = React.useState(false);

  // Определяем API методы в зависимости от типа объекта
  const getApiMethods = () => {
    switch (propertyType) {
      case 'foreign':
        return {
          list: AdminAPI.listForeignPropertyManagers,
          attach: AdminAPI.attachForeignPropertyManager,
          update: AdminAPI.updateForeignPropertyManager,
          delete: AdminAPI.deleteForeignPropertyManager,
        };
      case 'suburban':
        return {
          list: AdminAPI.listSuburbanPropertyManagers,
          attach: AdminAPI.attachSuburbanPropertyManager,
          update: AdminAPI.updateSuburbanPropertyManager,
          delete: AdminAPI.deleteSuburbanPropertyManager,
        };
      case 'commercial-rent':
        return {
          list: AdminAPI.listCommercialRentPropertyManagers,
          attach: AdminAPI.attachCommercialRentPropertyManager,
          update: AdminAPI.updateCommercialRentPropertyManager,
          delete: AdminAPI.deleteCommercialRentPropertyManager,
        };
      case 'parking':
        return {
          list: AdminAPI.listParkingPropertyManagers,
          attach: AdminAPI.attachParkingPropertyManager,
          update: AdminAPI.updateParkingPropertyManager,
          delete: AdminAPI.deleteParkingPropertyManager,
        };
      default:
        return {
          list: AdminAPI.listPropertyManagers,
          attach: AdminAPI.attachPropertyManager,
          update: AdminAPI.updatePropertyManager,
          delete: AdminAPI.deletePropertyManager,
        };
    }
  };

  const api = React.useMemo(() => getApiMethods(), [propertyType]);

  const load = React.useCallback(async () => {
    if (busy) return; // Предотвращаем повторные запросы
    
    setBusy(true);
    setMsg('');
    try {
      const [propResp, allResp] = await Promise.all([
        api.list(propertyId),
        AdminAPI.listManagers()
      ]);
      
      setPropertyManagers(Array.isArray(propResp?.managers) ? propResp.managers : []);
      setAllManagers(Array.isArray(allResp?.managers) ? allResp.managers : []);
    } catch (e) {
      console.error('Error loading managers:', e);
      setMsg(e.message || 'Не удалось загрузить менеджеров');
    } finally {
      setBusy(false);
    }
  }, [propertyId, propertyType, busy]);

  React.useEffect(() => { load(); }, [propertyId, propertyType]);

  React.useEffect(() => { 
    if (propertyId) {
      load(); 
    }
  }, [propertyId, propertyType]); // Убираем load из зависимостей

  const attachedManagerIds = propertyManagers.map(pm => pm.manager_id || pm.manager?.id);
  const availableManagers = allManagers.filter(m => !attachedManagerIds.includes(m.id));

  const handleAttach = async (managerId) => {
    setBusy(true);
    try {
      await api.attach(propertyId, {
        manager_id: managerId,
        is_primary: propertyManagers.length === 0, // первый менеджер автоматически основной
        sort_order: propertyManagers.length,
      });
      await load();
      setMsg('Менеджер назначен');
      setTimeout(() => setMsg(''), 2000);
    } catch (e) {
      setMsg(e.message || 'Ошибка назначения менеджера');
    } finally {
      setBusy(false);
    }
  };

  const handleDetach = async (linkId) => {
    if (!window.confirm('Убрать менеджера с объекта?')) return;
    setBusy(true);
    try {
      await api.delete(propertyId, linkId);
      await load();
      setMsg('Менеджер убран');
      setTimeout(() => setMsg(''), 2000);
    } catch (e) {
      setMsg(e.message || 'Ошибка удаления менеджера');
    } finally {
      setBusy(false);
    }
  };

  const togglePrimary = async (linkId, isPrimary) => {
    setBusy(true);
    try {
      await api.update(propertyId, linkId, { is_primary: !isPrimary });
      await load();
      setMsg('Статус менеджера изменен');
      setTimeout(() => setMsg(''), 2000);
    } catch (e) {
      setMsg(e.message || 'Ошибка изменения статуса');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h4 style={{ margin: 0 }}>Менеджеры ({propertyManagers.length})</h4>
        <Actions style={{ margin: 0 }}>
          <Btn onClick={() => setShowPicker(!showPicker)} disabled={busy}>
            {showPicker ? 'Скрыть список' : '+ Назначить менеджера'}
          </Btn>
        </Actions>
      </div>

      {msg && <div style={{ color: msg.includes('Ошибка') ? '#e66' : '#1b5e20', marginBottom: 8 }}>{msg}</div>}

      {showPicker && (
        <Card style={{ marginBottom: 16, background: '#f8f9fa' }}>
          <h5>Доступные менеджеры</h5>
          {availableManagers.length === 0 ? (
            <div style={{ color: '#777', padding: 10 }}>
              Все доступные менеджеры уже назначены на объект.
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 8 }}>
              {availableManagers.map((manager) => (
                <div
                  key={manager.id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: 12,
                    border: '1px solid var(--bdr)',
                    borderRadius: 4,
                    background: '#fff',
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 500 }}>
                      {manager.name_ru || manager.name || 'Без имени'}
                    </div>
                    {manager.name_en && (
                      <div style={{ fontSize: 12, color: '#666' }}>
                        {manager.name_en}
                      </div>
                    )}
                    <div style={{ fontSize: 12, color: '#777', marginTop: 2 }}>
                      {manager.phone && <span>📞 {manager.phone}</span>}
                      {manager.email && <span style={{ marginLeft: 8 }}>✉️ {manager.email}</span>}
                    </div>
                  </div>
                  <Btn
                    onClick={() => handleAttach(manager.id)}
                    disabled={busy}
                    style={{ fontSize: 12, padding: '4px 8px' }}
                  >
                    Назначить
                  </Btn>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {busy && <div style={{ opacity: 0.6 }}>Загрузка...</div>}

      <div style={{ display: 'grid', gap: 8 }}>
        {propertyManagers.map((propManager) => {
          const manager = propManager.manager || propManager;
          return (
            <div
              key={propManager.id}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: 12,
                border: '1px solid var(--bdr)',
                borderRadius: 4,
                background: propManager.is_primary ? '#e8f5e8' : '#fff',
              }}
            >
              <div>
                <div style={{ fontWeight: 500 }}>
                  {manager.name_ru || manager.name || 'Без имени'}
                  {propManager.is_primary && (
                    <span style={{ color: '#1b5e20', marginLeft: 8, fontSize: 12 }}>• Основной</span>
                  )}
                </div>
                {manager.name_en && (
                  <div style={{ fontSize: 12, color: '#666' }}>
                    {manager.name_en}
                  </div>
                )}
                <div style={{ fontSize: 12, color: '#777', marginTop: 2 }}>
                  {manager.phone && <span>📞 {manager.phone}</span>}
                  {manager.email && <span style={{ marginLeft: 8 }}>✉️ {manager.email}</span>}
                </div>
                {manager.position_ru && (
                  <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>
                    {manager.position_ru}
                  </div>
                )}
              </div>
              
              <Actions style={{ margin: 0 }}>
                <Btn
                  onClick={() => togglePrimary(propManager.id, propManager.is_primary)}
                  disabled={busy}
                  style={{ fontSize: 12, padding: '4px 8px' }}
                >
                  {propManager.is_primary ? 'Снять основного' : 'Сделать основным'}
                </Btn>
                <Btn
                  onClick={() => handleDetach(propManager.id)}
                  disabled={busy}
                  style={{ fontSize: 12, padding: '4px 8px', color: '#e66' }}
                >
                  Убрать
                </Btn>
              </Actions>
            </div>
          );
        })}
      </div>

      {propertyManagers.length === 0 && !busy && (
        <div style={{ textAlign: 'center', padding: 20, color: '#777' }}>
          Менеджеры не назначены. Нажмите "Назначить менеджера" для выбора из списка.
        </div>
      )}
    </Card>
  );
}